import * as Sentry from '@sentry/react';

import { isLocal } from '../utils/use-is-local';

import { scrubSensitiveData } from './data-scrub';

type ObservabilityConfig = {
  dsn: string;
  environment: string;
  enableBrowserTracing?: boolean;
  enableGraphQL?: boolean;
  hosts: string[];
};

function sentryEventScrub(event: Sentry.ErrorEvent) {
  const toScrub: (object | undefined)[] = [];

  if (event.extra) {
    toScrub.push(event.extra);
  }

  if (event.user) {
    toScrub.push(event.user);
  }

  if (event.breadcrumbs) {
    toScrub.push(event.breadcrumbs);
  }

  if (event.contexts) {
    toScrub.push(event.contexts);
  }

  if (event.tags) {
    toScrub.push(event.tags);
  }

  if (event?.request) {
    toScrub.push(event.request.headers);
    try {
      const body = typeof event.request.data === 'string' && JSON.parse(event.request.data);
      event.request.data = JSON.stringify(scrubSensitiveData(body));
    } catch {
      void 0;
    }
  }

  toScrub.filter((p) => p !== undefined).forEach((p) => scrubSensitiveData(p));
  return event;
}

const initializeObservability = (config: ObservabilityConfig) => {
  if (isLocal()) {
    return;
  }
  const integrations = [];
  if (config.enableBrowserTracing !== false) {
    integrations.push(Sentry.browserTracingIntegration());
  }

  if (config.enableGraphQL !== false) {
    integrations.push(
      Sentry.graphqlClientIntegration({
        endpoints: [...config.hosts, /\/graphql$/],
      })
    );
  }

  try {
    Sentry.init({
      beforeSend(event) {
        try {
          return sentryEventScrub(event);
        } catch {
          captureObservabilityMessage('scrubbing failed for event: ' + event.event_id);
          return event;
        }
      },
      dsn: config.dsn,
      environment: config.environment,
      integrations,
      sampleRate: 1,
      tracePropagationTargets: [...config.hosts, /^\//],
      tracesSampleRate: 1,
    });
  } catch {
    return;
  }
};

const observabilityMethodWrapper = <T extends unknown[]>(captureFn: (...args: T) => void) => {
  return (...args: T) => {
    if (isLocal()) {
      return;
    }
    captureFn(...args);
  };
};

const captureObservabilityError = observabilityMethodWrapper((error: unknown) => {
  const errorToCapture = error instanceof Error ? error : new Error(String(error));
  Sentry.captureException(errorToCapture);
});

const captureObservabilityMessage = observabilityMethodWrapper((message: string) => {
  Sentry.captureMessage(message);
});

export { captureObservabilityError, captureObservabilityMessage, initializeObservability, sentryEventScrub };
